import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExitToApp as LeaveIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalWebSocket } from '../contexts/GlobalWebSocketContext';
import { useTranslation } from 'react-i18next';
import { THEME_COLORS, THEME_RADIUS } from '../constants/theme';
import { DOCUMENT, documentAddButtonSx, getDocumentInitials } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';
import CsvImportDialog from './CsvImportDialog';
import PartnerCapacityDialog from './PartnerCapacityDialog';
import { partnerCapacityFromError } from '../utils/partnerPlan';
import { getParticipantDisplayName } from '../utils/participantName';

const TripMembers = forwardRef(({ tripId, isCreator, isPartner, onInviteClick, onMemberChange, memberCount }, ref) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addEventListener, subscribeToTrip, unsubscribeFromTrip } = useGlobalWebSocket();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [optimisticInvitations, setOptimisticInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [invitationToRemove, setInvitationToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [resendingInvitation, setResendingInvitation] = useState(null);
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [capacityOpen, setCapacityOpen] = useState(false);
  const [capacityError, setCapacityError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setRefreshing(true);
      
      // Fetch trip details to get consolidated participants
      const tripResponse = await api.get(`/trips/${tripId}`);
      const tripData = tripResponse.data;
      
      // Use all_participants if available (new consolidated format), 
      // otherwise fall back to separate members and invitations
      if (tripData.all_participants) {
        // New consolidated format - each person appears only once
        const participants = tripData.all_participants;
        
        // Separate actual members from invitations
        // Members: creator, member (these are users who have joined)
        // Invitations: pending, declined (NOT accepted - those should show as members)
        const actualMembers = participants.filter(p => p.status === 'creator' || p.status === 'member');
        const pendingInvitations = participants.filter(p => p.status === 'pending' || p.status === 'declined');
        
        setMembers(actualMembers);
        setInvitations(pendingInvitations);
      } else {
        // Legacy format - separate members and invitations (with potential duplicates)
        const currentMembers = tripData.members || [];
        setMembers(currentMembers);
        
        // Fetch invitations separately if not in consolidated format
        const invitationsResponse = await api.get(`/trips/${tripId}/invitations`);
        const currentInvitations = invitationsResponse.data || [];
        
        // Process invitations to show ALL invitations (pending, declined)
        // Don't filter out by member email - show all pending/declined invitations
        const processedInvitations = currentInvitations
          .filter(invitation => {
            // Only show pending or declined invitations (not accepted ones)
            return invitation.status === 'pending' || invitation.status === 'declined';
          })
          .map(invitation => ({
            ...invitation,
            status_text: invitation.status === 'pending' ? 'Pending' : 
                        invitation.status === 'declined' ? 'Declined' : invitation.status_text
          }));
        
        setInvitations(processedInvitations);
      }

    } catch (err) {
      setError('Failed to fetch trip data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tripId]);

  // Function to add optimistic invitation
  const addOptimisticInvitation = useCallback((email) => {
    const optimisticInvitation = {
      id: `optimistic-${Date.now()}`, // Temporary ID for optimistic update
      email: email,
      status: 'pending',
      status_text: 'Sending...',
      created_at: new Date().toISOString(),
      trip_id: tripId,
      isOptimistic: true // Flag to identify optimistic invitations
    };
    
    setOptimisticInvitations(prev => [...prev, optimisticInvitation]);
    
    // Remove the optimistic invitation after a timeout (in case WebSocket doesn't update)
    const timeoutId = setTimeout(() => {
      setOptimisticInvitations(prev => 
        prev.filter(inv => inv.id !== optimisticInvitation.id)
      );
    }, 15000); // Remove after 15 seconds (increased for slower networks)
    
    // Store timeout ID for cleanup if needed
    optimisticInvitation.timeoutId = timeoutId;
  }, [tripId]);

  // Function to remove optimistic invitation (when confirmed by server)
  const removeOptimisticInvitation = useCallback((email = null) => {
    setOptimisticInvitations(prev => {
      if (email) {
        // Remove specific optimistic invitation by email
        return prev.filter(inv => !(inv.isOptimistic && inv.email === email));
      } else {
        // Remove all optimistic invitations (fallback behavior)
        return prev.filter(inv => !inv.isOptimistic);
      }
    });
  }, []);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    addOptimisticInvitation,
    removeOptimisticInvitation
  }), [addOptimisticInvitation, removeOptimisticInvitation]);

  // Merge real invitations with optimistic ones
  const allInvitations = useMemo(() => {
    const realInvitationEmails = invitations.map(inv => inv.email);
    const validOptimistic = optimisticInvitations.filter(
      inv => !realInvitationEmails.includes(inv.email)
    );
    return [...invitations, ...validOptimistic];
  }, [invitations, optimisticInvitations]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Subscribe to trip for real-time updates
  useEffect(() => {
    if (subscribeToTrip) {
      subscribeToTrip(tripId);
    }
    return () => {
      if (unsubscribeFromTrip) {
        unsubscribeFromTrip(tripId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]); // Only depend on tripId to prevent infinite subscription loops

  // Set up WebSocket listener for real-time updates
  useEffect(() => {
    const removeMemberRemoved = addEventListener('member_removed', (data) => {
      if (data.trip_id === tripId) {
        // Remove the member from the local state
        setMembers(prev => prev.filter(member => member.id !== data.removed_user.id));
        
        // Show a notification about the member removal
        if (data.removed_user.id !== user?.id) {
          setSuccessMessage(`${data.removed_user.username} has ${data.action} the trip`);
        }
      }
    });

    const removeMemberAdded = addEventListener('member_added', (data) => {
      if (data.trip_id === tripId) {
        // Refresh data when a new member is added
        fetchData();
      }
    });

    const removeInvitationCancelled = addEventListener('invitation_cancelled', (data) => {
      if (data.trip_id === tripId) {
        // Remove the invitation from the local state
        setInvitations(prev => prev.filter(invitation => invitation.id !== data.invitation.id));
        
        // Show a notification about the invitation cancellation
        setSuccessMessage(`Invitation to ${data.invitation.email} has been cancelled`);
      }
    });

    const removeInvitationSent = addEventListener('invitation_sent', (data) => {
      console.log('[DEBUG] TripMembers received invitation_sent event:', data);
      if (data.trip_id === tripId) {
        console.log('[DEBUG] TripMembers processing invitation_sent for trip:', tripId);
        // Remove optimistic invitation for this specific email (if exists)
        removeOptimisticInvitation(data.email);
        
        // Refresh data when a new invitation is sent
        fetchData();
        
        // Show a notification about the new invitation
        setSuccessMessage(`Invitation sent to ${data.email}`);
      } else {
        console.log('[DEBUG] TripMembers ignoring invitation_sent for different trip:', data.trip_id);
      }
    });

    return () => {
      removeMemberRemoved();
      removeMemberAdded();
      removeInvitationCancelled();
      removeInvitationSent();
    };
  }, [addEventListener, tripId, user?.id, fetchData, removeOptimisticInvitation]);

  // Real-time updates handled by WebSocket listeners above - no polling needed

  const handleRefresh = () => {
    fetchData();
  };

  const handleRemoveMember = (member) => {
    setMemberToRemove(member);
    setInvitationToRemove(null);
    setRemoveDialogOpen(true);
  };

  const handleRemoveInvitation = (invitation) => {
    setInvitationToRemove(invitation);
    setMemberToRemove(null);
    setRemoveDialogOpen(true);
  };

  const confirmRemoveMember = async () => {
    if (memberToRemove) {
      await removeMember();
    } else if (invitationToRemove) {
      await removeInvitation();
    }
  };

  const removeMember = async () => {
    if (!memberToRemove) return;

    try {
      setRemoving(true);
      setError(null);

      const response = await api.delete(`/trips/${tripId}/members/${memberToRemove.id}`);
      
      setSuccessMessage(response.data.message);
      setRemoveDialogOpen(false);
      setMemberToRemove(null);
      
      // Refresh the data to reflect changes
      await fetchData();
      
      // Notify parent component about member count change
      if (onMemberChange) {
        onMemberChange();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
      console.error('Error removing member:', err);
    } finally {
      setRemoving(false);
    }
  };

  const removeInvitation = async () => {
    if (!invitationToRemove) return;

    try {
      setRemoving(true);
      setError(null);

      const response = await api.delete(`/trips/${tripId}/invitations/${invitationToRemove.invitation_id || invitationToRemove.id}`);
      
      setSuccessMessage(response.data.message);
      setRemoveDialogOpen(false);
      setInvitationToRemove(null);
      
      // Refresh the data to reflect changes
      await fetchData();
      
      // Notify parent component about member count change
      if (onMemberChange) {
        onMemberChange();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel invitation');
      console.error('Error cancelling invitation:', err);
    } finally {
      setRemoving(false);
    }
  };

  const cancelRemoveMember = () => {
    setRemoveDialogOpen(false);
    setMemberToRemove(null);
    setInvitationToRemove(null);
  };

  const canRemoveMember = (member) => {
    // Trip creator or partner can remove any member (partners can remove anyone, creators can remove anyone except themselves)
    if (isPartner) {
      return true; // Partners can remove any member including the creator
    }
    if (isCreator && member.id !== user?.id) {
      return true;
    }
    // Regular members can only remove themselves (leave the trip)
    if (!isCreator && !isPartner && member.id === user?.id) {
      return true;
    }
    return false;
  };

  const getRemoveButtonText = (member) => {
    if (!member) return 'Remove';
    if (member.id === user?.id) {
      return 'Leave Trip';
    }
    return 'Remove';
  };

  const getRemoveButtonIcon = (member) => {
    if (!member) return <DeleteIcon />;
    if (member.id === user?.id) {
      return <LeaveIcon />;
    }
    return <DeleteIcon />;
  };

  const getDialogTitle = () => {
    if (memberToRemove) {
      return memberToRemove.id === user?.id ? 'Leave Trip' : 'Remove Member';
    }
    if (invitationToRemove) {
      return 'Cancel Invitation';
    }
    return 'Confirm Action';
  };

  const getConfirmationText = () => {
    if (memberToRemove) {
      if (memberToRemove.id === user?.id) {
        return `Are you sure you want to leave this trip? You will no longer have access to trip information, chat, or itinerary.`;
      }
      return `Are you sure you want to remove ${memberToRemove.username} from this trip? They will lose access to all trip information.`;
    }
    
    if (invitationToRemove) {
      const invitedName = invitationToRemove.invited_user_name || invitationToRemove.email;
      return `Are you sure you want to cancel the invitation to ${invitedName}? They will no longer be able to join this trip using this invitation.`;
    }
    
    return '';
  };

  const getActionButtonText = () => {
    if (removing) return 'Processing...';
    
    if (memberToRemove) {
      return memberToRemove.id === user?.id ? 'Leave Trip' : 'Remove Member';
    }
    if (invitationToRemove) {
      return 'Cancel Invitation';
    }
    return 'Confirm';
  };

  const getActionButtonIcon = () => {
    if (removing) return <CircularProgress size={16} />;
    
    if (memberToRemove) {
      return getRemoveButtonIcon(memberToRemove);
    }
    if (invitationToRemove) {
      return <DeleteIcon />;
    }
    return <DeleteIcon />;
  };

  const handleResendInvitation = async (invitation) => {
    setResendingInvitation(invitation.id);
    try {
      await api.post(`/trips/${tripId}/invite`, { email: invitation.email });
      
      // Show appropriate message based on current status
      const actionMessage = invitation.status === 'declined' 
        ? `Re-invitation sent to ${invitation.email}` 
        : `Invitation resent to ${invitation.email}`;
      setSuccessMessage(actionMessage);
      
      // Refresh data to show updated timestamp
      setTimeout(() => {
        fetchData();
      }, 1000);
    } catch (err) {
      console.error('Error resending invitation:', err);
      const capacity = isPartner ? partnerCapacityFromError(err) : null;
      if (capacity) {
        setCapacityError(capacity);
        setCapacityOpen(true);
        setError(capacity.message);
        return;
      }
      const errorMessage = err.response?.data?.error || 'Failed to resend invitation';
      setError(errorMessage);
      
      // Show more specific error message based on the error
      if (errorMessage.includes('SMTP') || errorMessage.includes('email')) {
        setError('Failed to send invitation email. Please try again later.');
      } else if (errorMessage.includes('permission')) {
        setError('You do not have permission to resend invitations.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setResendingInvitation(null);
    }
  };

  const handleCsvImportComplete = (summary) => {
    let message = `CSV import completed! ${summary.successful} invitations sent successfully.`;
    if (summary.skipped > 0 && !isPartner) {
      message += ` ${summary.skipped} emails were skipped due to member limits. Upgrade your plan to add more members.`;
    } else if (summary.skipped > 0) {
      message += ` ${summary.skipped} emails were skipped.`;
    }
    setSuccessMessage(message);
    fetchData(); // Refresh the members list
  };

  useEffect(() => {
    if (error && error.includes('invitation')) {
      removeOptimisticInvitation();
    }
  }, [error, removeOptimisticInvitation]);

  if (loading && !refreshing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <>
      <Box>
        <DocumentSectionHead
          title={t('tripMembers.title')}
          meta={t('tripMembers.countOnly', { count: members.length || memberCount || 0 })}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1, mt: -1 }}>
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
            aria-label="refresh"
            sx={{ color: DOCUMENT.ink3 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {(members.length === 0 && ((isCreator || isPartner) ? allInvitations.length === 0 : true)) ? (
          <Typography sx={{ color: DOCUMENT.ink3 }}>{t('tripMembers.noMembers')}</Typography>
        ) : (
          <Box>
            {members.map((member, index) => {
              const canRemove = canRemoveMember(member);
              const isCreatorMember = member.status === 'creator' || member.display_role === 'creator';
              const displayName = getParticipantDisplayName(member);
              return (
                <Box
                  key={member.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.75,
                    py: 1.75,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: index === 1 ? DOCUMENT.accent : DOCUMENT.oxblood,
                      color: index === 1 ? DOCUMENT.oxblood : DOCUMENT.ivory,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      letterSpacing: '0.04em',
                      backgroundImage: member.avatar ? `url(${member.avatar})` : 'none',
                      backgroundSize: 'cover',
                      flexShrink: 0,
                    }}
                  >
                    {!member.avatar ? getDocumentInitials(displayName) : null}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: 15, color: DOCUMENT.ink }}>
                      {displayName}
                    </Typography>
                    <Typography sx={{ fontStyle: 'normal', fontSize: '0.78125rem', color: DOCUMENT.ink3 }}>
                      {isCreatorMember
                        ? t('tripMembers.tripCreator', { email: member.email || '' })
                        : member.date_joined_or_invited
                          ? t('tripMembers.joined', { date: new Date(member.date_joined_or_invited).toLocaleDateString() })
                          : member.email}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      ml: 'auto',
                      fontSize: 10,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: isCreatorMember ? DOCUMENT.accent : DOCUMENT.ink3,
                    }}
                  >
                    {isCreatorMember ? t('tripMembers.creator') : (member.display_role || t('tripMembers.member'))}
                  </Typography>
                  {canRemove && (
                    <Tooltip title={getRemoveButtonText(member)}>
                      <IconButton
                        onClick={() => handleRemoveMember(member)}
                        size="small"
                        sx={{ color: DOCUMENT.ink3 }}
                      >
                        {getRemoveButtonIcon(member)}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
            {(isCreator || isPartner) && allInvitations.map((invitation) => (
              <Box
                key={invitation.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  py: 1.75,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  opacity: invitation.isOptimistic ? 0.8 : 1,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: DOCUMENT.oxblood,
                    color: DOCUMENT.ivory,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {getDocumentInitials(invitation.invited_user_name || getParticipantDisplayName(invitation))}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 500, fontSize: 15, color: DOCUMENT.ink }}>
                    {invitation.invited_user_name || getParticipantDisplayName(invitation)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78125rem', color: DOCUMENT.ink3 }}>
                    {invitation.isOptimistic
                      ? t('tripMembers.sending')
                      : t('tripMembers.inviteSent', {
                          date: new Date(invitation.date_joined_or_invited || invitation.created_at).toLocaleDateString(),
                        })}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    ml: 'auto',
                    fontSize: 10,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: DOCUMENT.ink3,
                  }}
                >
                  {invitation.status_text || t('tripMembers.pending')}
                </Typography>
                {(isCreator || isPartner) && invitation.status === 'pending' && !invitation.isOptimistic && (
                  <Tooltip title="Cancel Invitation">
                    <IconButton
                      onClick={() => handleRemoveInvitation(invitation)}
                      size="small"
                      sx={{ color: DOCUMENT.ink3 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {(isCreator || isPartner) && (invitation.status === 'pending' || invitation.status === 'declined') && !invitation.isOptimistic && (
                  <Tooltip title={invitation.status === 'declined' ? "Re-invite User" : "Resend Invitation"}>
                    <IconButton
                      onClick={() => handleResendInvitation(invitation)}
                      size="small"
                      sx={{ color: DOCUMENT.accent }}
                      disabled={resendingInvitation === invitation.id}
                    >
                      {resendingInvitation === invitation.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SendIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                {(isCreator || isPartner) && invitation.status === 'declined' && !invitation.isOptimistic && (
                  <Tooltip title="Remove Declined Invitation">
                    <IconButton
                      onClick={() => handleRemoveInvitation(invitation)}
                      size="small"
                      sx={{ color: DOCUMENT.ink3 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        )}
        {(isCreator || isPartner) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button onClick={onInviteClick} sx={documentAddButtonSx}>
              {t('tripMembers.inviteMember')}
            </Button>
            <Button onClick={() => setCsvImportDialogOpen(true)} sx={documentAddButtonSx}>
              {t('tripMembers.importCsv')}
            </Button>
          </Box>
        )}
      </Box>

      {/* Remove Member Confirmation Dialog */}
      <Dialog 
        open={removeDialogOpen} 
        onClose={cancelRemoveMember}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {getDialogTitle()}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getConfirmationText()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={cancelRemoveMember}
            disabled={removing}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={confirmRemoveMember}
            disabled={removing}
            startIcon={getActionButtonIcon()}
            sx={{
              px: 3,
              borderRadius: THEME_RADIUS.small,
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: memberToRemove?.id === user?.id ? THEME_COLORS.status.warning : THEME_COLORS.status.error,
              color: 'white',
              '&:hover': {
                backgroundColor: memberToRemove?.id === user?.id ? '#B45309' : '#B91C1C',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                backgroundColor: '#999',
                color: 'white',
                opacity: 0.6
              }
            }}
          >
            {getActionButtonText()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />

      {/* CSV Import Dialog */}
      <CsvImportDialog
        open={csvImportDialogOpen}
        onClose={() => setCsvImportDialogOpen(false)}
        tripId={tripId}
        isPartner={isPartner}
        onImportComplete={handleCsvImportComplete}
        onCapacityLimit={(capacity) => {
          setCsvImportDialogOpen(false);
          setCapacityError(capacity);
          setCapacityOpen(true);
        }}
      />

      {isPartner && capacityOpen && (
        <PartnerCapacityDialog
          open={capacityOpen}
          limitError={capacityError}
          onClose={() => setCapacityOpen(false)}
          onSuccess={() => {
            setCapacityOpen(false);
            fetchData();
          }}
        />
      )}
    </>
  );
});

export default TripMembers; 